import { convertSeedsToBagsForProduct } from 'utils/convertSeedsToBagsForProduct';
import { CropConstants } from './constants/Crop';
import { Country } from 'base/types/Countries';
import { ProductSeed } from 'base/types/Products';
import { getBagCountForSouthAfrica } from './getSeedRateAndBagSizeForSouthAfrica';

type GetBagsCountParams = {
  product?: ProductSeed;
  hectares: number;
  defaultSeedingRate: number;
  countryList: Country[];
  countryCode?: string | null;
  cropId?: string | number;
  spatialClass?: string;
};
const getBagCountForCrop = (
  country: Country,
  defaultSeedingRate: number,
  hectares: number,
  cropName: string,
  cropBagCapacity: number
) => {
  // Use Netherlands bag size if the country is Netherlands
  if (
    country.shortCode === CropConstants.NETHERLANDS_CODE ||
    country.alpha2 === CropConstants.NETHERLANDS_CODE ||
    (country.name && country.name.toUpperCase() === 'NETHERLANDS')
  ) {
    return (defaultSeedingRate * hectares) / CropConstants.NETHERLANDS_BAG_SIZE;
  }

  const defaultBagSize = country.crops.find((crop) => crop.CropName === cropName)?.BagSize;
  return defaultBagSize
    ? (defaultSeedingRate * hectares) / defaultBagSize
    : (defaultSeedingRate * hectares) / cropBagCapacity;
};

export const getBagsCount = (params: GetBagsCountParams, doNotApplyCeil?: boolean): number => {
  const { product, hectares, defaultSeedingRate, countryList, countryCode, cropId, spatialClass } =
    params;
  const {
    SUNFLOWER_SMALLCASE,
    SUNFLOWER_BAG_CAPACITY,
    SUNFLOWER_CROP_ID,
    CORN_BAG_CAPACITY,
    CORN_SMALLCASE,
    SUMMER_CORN_SMALLCASE,
    SUMMER_CORN_CROP_ID,
    BARLEY_CROP_ID,
    BARLEY,
    BARLEY_BAG_CAPACITY,
    NETHERLANDS_CODE,
    NETHERLANDS_BAG_SIZE,
  } = CropConstants;
  let bags = convertSeedsToBagsForProduct(defaultSeedingRate, hectares, product, countryCode);
  if (bags === 0) {
    const normalizedCountryCode = countryCode?.trim()?.toUpperCase();
    const country = countryList.find(
      (countryItem) =>
        (normalizedCountryCode && countryItem.shortCode === normalizedCountryCode) ||
        (normalizedCountryCode && countryItem.alpha2.toUpperCase() === normalizedCountryCode) ||
        (normalizedCountryCode && countryItem.name.toUpperCase() === normalizedCountryCode)
    );
    if (country) {
      if (cropId?.toString() === SUNFLOWER_CROP_ID.toString()) {
        bags = getBagCountForCrop(
          country,
          defaultSeedingRate,
          hectares,
          SUNFLOWER_SMALLCASE,
          SUNFLOWER_BAG_CAPACITY
        );
      } else if (cropId === SUMMER_CORN_CROP_ID.toString()) {
        bags = getBagCountForCrop(
          country,
          defaultSeedingRate,
          hectares,
          SUMMER_CORN_SMALLCASE,
          SUNFLOWER_BAG_CAPACITY
        );
      } else if (cropId === BARLEY_CROP_ID) {
        bags = getBagCountForCrop(
          country,
          defaultSeedingRate,
          hectares,
          BARLEY,
          BARLEY_BAG_CAPACITY
        );
      } else if (countryCode === CropConstants.SOUTH_AFRICA_CODE) {
        bags = getBagCountForSouthAfrica(
          country,
          spatialClass,
          product?.commercialName ?? '',
          hectares
        );
      } else {
        bags = getBagCountForCrop(
          country,
          defaultSeedingRate,
          hectares,
          CORN_SMALLCASE,
          CORN_BAG_CAPACITY
        );
      }
    }
  }
  return doNotApplyCeil ? parseFloat(bags?.toFixed(2)) : Math.ceil(bags);
};
